import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Send, Trash2, Loader2 } from "lucide-react";

/**
 * Reusable real-time-ish chat panel.
 * Polls the API every 5s for new messages.
 *
 * Props:
 *  - channel: "bureau" | "members"
 *  - apiClient: a fn (method, path, body?) -> Promise<data>
 *  - canDelete: boolean (admin)
 *  - currentLabel: string (display "Vous" tag)
 *  - placeholder: string
 */
export default function ChatPanel({ channel, apiClient, canDelete, currentLabel, placeholder }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await apiClient("get", `/forum/messages?channel=${channel}`);
      setMessages(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les messages");
    } finally {
      setLoading(false);
    }
  }, [apiClient, channel]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    try {
      await apiClient("post", "/forum/messages", { channel, content });
      setDraft("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await apiClient("delete", `/forum/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div data-testid={`chat-panel-${channel}`} className="flex flex-col h-[calc(100vh-280px)] min-h-[420px] bg-white border border-brand-border rounded-xl overflow-hidden">
      <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#FBFAF6]">
        {loading && (
          <div className="text-center text-sm text-gray-400 py-10">
            <Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-sm text-gray-400 py-10">
            Aucun message pour l&apos;instant. Lancez la conversation !
          </div>
        )}
        {messages.map((m) => {
          const isMine = m.author_label === currentLabel;
          return (
            <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] group ${isMine ? "items-end" : "items-start"} flex flex-col`}>
                <div className="flex items-center gap-2 mb-1 text-[11px] text-gray-500">
                  <span className="font-medium text-brand-navy">{m.author_label || "Anonyme"}</span>
                  <Badge
                    variant="outline"
                    className={`text-[9px] py-0 px-1.5 ${
                      m.author_role === "bureau"
                        ? "border-brand-navy/30 text-brand-navy"
                        : "border-brand-orange/30 text-brand-orange"
                    }`}
                  >
                    {m.author_role === "bureau" ? "Bureau" : "Membre"}
                  </Badge>
                  <span className="text-gray-400">· {fmtTime(m.created_at)}</span>
                </div>
                <div className={`relative inline-block px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                  m.is_announcement
                    ? "bg-amber-50 border border-amber-300 text-gray-800 rounded-bl-sm"
                    : isMine
                      ? "bg-brand-navy text-white rounded-br-sm"
                      : m.author_role === "bureau"
                        ? "bg-white border border-brand-navy/20 text-gray-800 rounded-bl-sm"
                        : "bg-white border border-brand-border text-gray-800 rounded-bl-sm"
                }`}>
                  {m.content}
                  {canDelete && (
                    <button
                      data-testid={`del-msg-${m.id}`}
                      onClick={() => remove(m)}
                      className={`absolute ${isMine ? "-left-7" : "-right-7"} top-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700`}
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="border-t border-brand-border bg-white p-3 flex items-end gap-2">
        <Textarea
          data-testid={`chat-input-${channel}`}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder || "Écrivez votre message… (Entrée pour envoyer, Maj+Entrée = nouvelle ligne)"}
          className="resize-none min-h-[42px] max-h-[140px]"
        />
        <Button
          type="submit"
          data-testid={`chat-send-${channel}`}
          disabled={sending || !draft.trim()}
          className="bg-brand-orange hover:bg-[#D04A06] text-white h-[42px]"
        >
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </Button>
      </form>
    </div>
  );
}
